<template>
  <article
    ref="card"
    class="js-product-card ProductCard"
    itemscope
    itemtype="https://schema.org/Product"
    itemprop="itemListElement"
    :data-product-id="formatID(selectedProduct.id)"
    :data-variant-id="formatID(featuredVariant.id)"
  >
    <div class="ProductCard-ImageWrapper" :class="loadedMedia">
      <a :href="cardUrl" tabindex="-1" aria-hidden="true">
        <picture v-if="featuredImage">
          <img
            loading="lazy"
            itemprop="image"
            :src="featuredImage.url"
            alt="Featured Image"
            role="presentation"
            @load="transitionMedia"
          />
          <img
            v-if="hoverImage"
            loading="lazy"
            :src="hoverImage.url"
            alt="Featured Hover Image"
            role="presentation"
            class="ProductCard__ProductImg ProductCard__ProductImg--Hover"
          />
        </picture>
        <ProductCardBadging :item="item" :variant="featuredVariant" />
      </a>
      <template v-if="quickViewReady">
        <button
          :data-ht-element="quickActionProperty"
          role="button"
          class="ProductCard__QuickView"
          @click="handleQuickAction"
        >
          <Icon icon="qv-plus" />
          <span
            class="t5 ProductCard__QuickView-Text"
            data-color="night-sky-dark"
          >
            {{ quickActionText }}
          </span>
        </button>
      </template>
    </div>
    <a :href="cardUrl">
      <Eyebrow :item="item" :ready="ready" :location="Location.ProductCard" />
      <div class="ProductCard__Title-Container">
        <p class="t2" data-ht-element="product-title">
          {{ item.title }}
        </p>
        <WishlistButton
          :empi="formatID(selectedProduct.id)"
          :epi="formatID(featuredVariant.id)"
          :dt="item.title"
          :du="cardUrl"
          source="plp"
          :cprops="{ selectedOption, handle: item.handle }"
        />
      </div>
      <Price :item="item" :location="Location.ProductCard" :ready="ready" />

      <ReviewStars
        v-if="reviewsProduct"
        :product="reviewsProduct"
        :location="Location.ProductCard"
      />
    </a>
    <ProductCardSwatches
      v-if="swatches.length > 0"
      :swatches="swatches"
      :all-options="item.options"
      :selected-options="featuredVariant.selectedOptions"
      :selection-listener="false"
      :hidden-options="hiddenOptions"
      :url="cardUrl"
      @update-card="updateCard"
    />
    <Teleport v-if="modalOpen && modalItem" to="#quickViewModal">
      <ProductModal
        :product="modalItem"
        :location="Location.QuickView"
        :reviews-product="reviewsProduct"
        :terms="terms"
        @toggle-modal="toggleModal"
      />
    </Teleport>
  </article>
</template>

<script setup>
// Vue core
import { ref, onMounted, computed } from 'vue';
import { Location } from '../Global/Location.types';

// Utilities
import { deepClone } from '@utils/performance';
import { formatID, parseTag } from '@utils/format';
import { fetchStorefront, clean } from '@utils/GraphQLClient';

// Composables
import { blacklistedVariantKeys } from './Composables/Utilities';
import { VARIANT_FROM_SELECTIONS } from './Composables/Queries';
import { quickAdd } from '../Global/Composables/ProductCard';

// Global components
import Icon from '../Global/Icon.vue';
import Price from '../Global/Price.vue';
import ReviewStars from '../Global/ReviewStars.vue';
import ProductCardSwatches from '../Global/ProductCardSwatches.vue';
import Eyebrow from '../Global/Eyebrow.vue';
import WishlistButton from '../Global/WishlistButton.vue';

// Local components
import ProductModal from '../Product/ProductModal.vue';
import ProductCardBadging from './ProductCardBadging.vue';

const observer = ref(null);
const card = ref(null);
const swatches = ref([]);
const inView = ref(false);
const loaded = ref(false);
const interacted = ref(false);
const selectedVariant = ref(null);
const modalOpen = ref(false);
const modalItem = ref(false);

const props = defineProps({
  item: {
    type: Object,
    required: true,
  },
  selectionListener: {
    type: Boolean,
    default: false,
  },
  ready: {
    type: Boolean,
    default: false,
  },
  appliedFilters: {
    type: Array,
    required: false,
    default: () => [],
  },
  terms: {
    type: Object,
    required: false,
    default: () => ({}),
  },
});

const selectedOption = computed(() => {
  const tags = props?.item?.tags || [];
  const galleryTag = parseTag(tags, 'PDP Gallery Option');
  const visualIndex = props.item.options.findIndex(
    (option) => option.name === galleryTag
  );

  const optionIndex = visualIndex === -1 ? 0 : visualIndex;
  return featuredVariant.value.selectedOptions[optionIndex]?.value;
});

const featuredImage = computed(() => {
  const variant = featuredVariant.value ?? false;
  if (!variant) return false;

  const image = variant.image;
  return image;
});

const hoverImage = computed(() => {
  const variant = featuredVariant.value ?? false;
  if (!variant) return false;

  const image = variant?.hoverImage?.reference?.image ?? false;
  return image;
});

const featuredVariant = computed(() => {
  const item = props?.item || {};
  const variants = selectedVariant.value
    ? [selectedVariant.value]
    : item?.variants || false;
  if (!variants) return false;

  const appliedFilters = props.appliedFilters.length > 0 ? true : false;
  const variantMetafieldsApplied =
    appliedFilters &&
    props.appliedFilters.filter((facet) => {
      return (
        facet?.variantMetafield &&
        !blacklistedVariantKeys.includes(facet.variantMetafield.key)
      );
    }).length > 0;

  let variant = variants[0];

  if (!variantMetafieldsApplied) {
    const hasFeaturedSku = item.featuredSku?.reference ?? false;
    variant =
      hasFeaturedSku && !interacted.value
        ? item.featuredSku.reference
        : variants[0];
  }

  return variant;
});

const selectedProduct = computed(() => {
  const selectedVariant = featuredVariant.value ?? false;

  if (!selectedVariant) return props.item;

  return selectedVariant.product;
});

const cardUrl = computed(() => {
  const tags = props.item.tags ?? [];
  const base = `/products/${props.item.handle}`;
  const variant = featuredVariant.value;
  if (!variant) return base;

  const isBundle = tags.includes('Product Bundle') ?? false;
  const isSteppedBundle = isBundle && !tags.includes('Auto-Bundle');
  const selectedVariant = formatID(variant.id);
  const param = isSteppedBundle ? `component0` : `variant`;

  let pdpTemplate = '';
  const data = window.Brooklinen?.pdpRedirect?.[0];
  if (data?.pdp_template) {
    pdpTemplate = `&view=${data?.pdp_template}`;
  }

  return `${base}?${param}=${selectedVariant}${pdpTemplate}`;
});

const reviewsProduct = computed(() => {
  const tags = props.item.tags ?? [];
  const hideReviews = tags.includes('Hide Zero Reviews');
  if (hideReviews) return false;

  const hasTag =
    tags.filter((tag) => tag.includes('Review ProductID')).length > 0;
  return hasTag ? parseTag(tags, 'Review ProductID') : formatID(props.item.id);
});

const isSingleProduct = computed(() => {
  return props.item.options.find((option) =>
    option.values.includes('Default Title')
  )
    ? true
    : false;
});

const quickActionText = computed(() => {
  const text = isSingleProduct.value ? `Quick Add` : `Quick View`;
  return text;
});

const quickActionProperty = computed(() => {
  const property = isSingleProduct.value ? `quick-add` : `quick-view`;
  return `${property}-button`;
});

const hiddenOptions = computed(() => {
  const tags = props?.item?.tags ?? [];
  return tags
    .filter((tag) => tag.includes('Hide Option Value:'))
    .map((tag) => tag.split(':')[1].trim());
});

const updateCard = async (selection) => {
  const { selectedOptions } = featuredVariant.value;
  const colorIndex = selectedOptions.findIndex(
    (option) => option.name === 'Color'
  );

  const currentColor = selectedOptions[colorIndex].value;

  if (currentColor === selection.value) return;

  interacted.value = true;

  const updatedOptions = deepClone(selectedOptions).map((option) => {
    if (option.name === 'Color') {
      return selection;
    }
    return option;
  });

  const request = {
    query: VARIANT_FROM_SELECTIONS,
    variables: {
      handle: props.item.handle,
      options: updatedOptions,
    },
  };

  const { data } = await fetchStorefront(request).then((res) => {
    return clean(res);
  });

  let match = data.product.variantBySelectedOptions;

  if (!match) {
    /**
     * No variant exists for {selected color + previously-selected size}.
     * Prefer an available variant matching the selected color before
     * falling through to featuredVariant's variants[0] default.
     */
    const variants = props.item?.variants ?? [];
    const byColor = variants.filter((variant) =>
      variant.selectedOptions.some(
        (option) => option.name === 'Color' && option.value === selection.value
      )
    );
    match =
      byColor.find((variant) => variant.quantityAvailable > 0) ??
      byColor[0] ??
      null;
  }

  selectedVariant.value = match;
};

const loadedMedia = computed(() =>
  inView.value && loaded.value ? 'ProductCard-ImageWrapper--Loaded' : ''
);
const transitionMedia = () => (loaded.value = true);

const quickViewReady = computed(() => {
  const tags = props?.item?.tags ?? [];
  const isBundle = tags.find((tag) => tag.includes('Product Bundle'))
    ? true
    : false;
  if (loadedMedia.value !== '' && !isBundle) {
    return true;
  }

  return false;
});

const unobserve = () => {
  if (observer.value) observer.value.disconnect();
};

const initializeObserver = () => {
  unobserve();
  observer.value = new IntersectionObserver(
    (entries) => {
      const { isIntersecting } = entries[0];
      if (isIntersecting) {
        inView.value = true;
        unobserve();
      }
    },
    { threshold: 0.6 }
  );
};

const observeCard = () => {
  unobserve();
  observer.value.observe(card.value);
};

const setSwatches = () => {
  const options = props?.item?.options || [];
  const color = options.find((option) => option.name === 'Color');
  if (!color) return;

  swatches.value = Array.from(new Set([...color.values]));
};

const toggleModal = (item) => {
  modalOpen.value = !modalOpen.value;

  if (modalOpen.value) {
    const current = deepClone(featuredVariant.value);
    modalItem.value = item;
    modalItem.value.selectedVariant = current;
    modalItem.value.selectedOptions = current.selectedOptions;
    modalItem.value.edit = true;
  } else {
    modalItem.value = false;
  }
};

const handleQuickAction = () => {
  const item = deepClone(props.item);
  return isSingleProduct.value ? quickAdd(item) : toggleModal(item);
};

onMounted(() => {
  setSwatches();
  initializeObserver();
  observeCard();
});
</script>
